import { CaseNotificationData, NotificationMessageType } from '@/api/apis/communication';

export type BulkNotificationProps = {
  data: CaseNotificationData;
  notificationType:
    | NotificationMessageType.CaseBulkSwapOperationCompleted
    | NotificationMessageType.CaseBulkAssignOperationCompleted
    | NotificationMessageType.CaseBulkDetachOperationCompleted
    | NotificationMessageType.CaseBulkOperationPrimaryElevation;
};

const getSuccessHeading = ({ data, notificationType }: BulkNotificationProps) => {
  const { AssignedUserName, DetachedUserName, RoleName, IsAssignedAsPrimary } = data;
  const primaryString = IsAssignedAsPrimary === 'True' ? ' and was made Primary' : '';

  switch (notificationType) {
    case NotificationMessageType.CaseBulkAssignOperationCompleted:
      return `${AssignedUserName} with the ${RoleName} role was successfully assigned to the following case(s)${primaryString}:`;

    case NotificationMessageType.CaseBulkDetachOperationCompleted:
      return `${DetachedUserName} with the ${RoleName} role was successfully detached from the following case(s):`;

    case NotificationMessageType.CaseBulkSwapOperationCompleted:
      return `${AssignedUserName} replaced ${DetachedUserName} in the ${RoleName} role${primaryString} on the following case(s):`;

    default:
      return '';
  }
};

const getPartialSuccessHeading = ({ data, notificationType }: BulkNotificationProps) => {
  const { AssignedUserName, DetachedUserName, RoleName } = data;

  switch (notificationType) {
    case NotificationMessageType.CaseBulkAssignOperationCompleted:
      return `${AssignedUserName} with the ${RoleName} role was successfully assigned on some of the selected case(s).`;

    case NotificationMessageType.CaseBulkDetachOperationCompleted:
      return `${DetachedUserName} with the ${RoleName} role was successfully detached from some of the selected case(s).`;

    case NotificationMessageType.CaseBulkSwapOperationCompleted:
      return `${AssignedUserName} replaced ${DetachedUserName} in the ${RoleName} role on some of the selected case(s).`;

    default:
      return '';
  }
};

const getFailureHeading = (
  { data, notificationType }: BulkNotificationProps,
  totalFailures: number,
  isOnlyCertFailure: boolean,
  isOnlyRegularFailure: boolean,
) => {
  const { AssignedUserName, DetachedUserName, RoleName } = data;

  if (isOnlyCertFailure) {
    const reasonSuffix =
      'failed because the user’s organ certifications contradict the organ types of the following case(s):';
    if (notificationType === NotificationMessageType.CaseBulkSwapOperationCompleted) {
      return `Replacing ${DetachedUserName} with ${AssignedUserName} in the ${RoleName} role ${reasonSuffix}`;
    }
    if (notificationType === NotificationMessageType.CaseBulkAssignOperationCompleted) {
      return `Assigning ${AssignedUserName} to the ${RoleName} role ${reasonSuffix}`;
    }
  }

  if (isOnlyRegularFailure) {
    switch (notificationType) {
      case NotificationMessageType.CaseBulkAssignOperationCompleted:
        return `Assigning ${AssignedUserName} to the ${RoleName} role failed for the following case(s):`;

      case NotificationMessageType.CaseBulkDetachOperationCompleted:
        return `Detaching ${DetachedUserName} from the ${RoleName} role failed for the following case(s):`;

      case NotificationMessageType.CaseBulkSwapOperationCompleted:
        return `Replacing ${DetachedUserName} with ${AssignedUserName} in the ${RoleName} role failed for the following case(s):`;
      default:
        return '';
    }
  }

  switch (notificationType) {
    case NotificationMessageType.CaseBulkAssignOperationCompleted:
      return `Assigning ${AssignedUserName} to the ${RoleName} role failed for ${totalFailures} case(s).`;

    case NotificationMessageType.CaseBulkDetachOperationCompleted:
      return `Detaching ${DetachedUserName} from the ${RoleName} role failed for ${totalFailures} case(s).`;

    case NotificationMessageType.CaseBulkSwapOperationCompleted:
      return `Replacing ${DetachedUserName} with ${AssignedUserName} in the ${RoleName} role failed for ${totalFailures} case(s).`;

    default:
      return '';
  }
};

export const getCasesParamaters = (props: BulkNotificationProps) => {
  const { data } = props;
  const successCount = JSON.parse(data?.SuccessfulCases || '[]').length;
  const failCount = JSON.parse(data?.FailedCases || '[]').length;
  const certFailCount = JSON.parse(data?.FailedCasesWithCertificationReason || '[]').length;

  const hasSuccess = successCount > 0;
  const hasCertificateFailure = certFailCount > 0;
  const hasRegularFailure = failCount > 0;
  const hasFailure = hasCertificateFailure || hasRegularFailure;
  const isOnlyCertFailure = !hasRegularFailure && hasCertificateFailure;
  const isOnlyRegularFailure = hasRegularFailure && !hasCertificateFailure;
  const totalFailures = failCount + certFailCount;

  return {
    hasSuccess,
    hasFailure,
    hasCertificateFailure,
    hasRegularFailure,
    isOnlyCertFailure,
    isOnlyRegularFailure,
    totalFailures,
  };
};

export const getBulkNotificationHeading = (props: BulkNotificationProps) => {
  const { hasFailure, hasSuccess, isOnlyCertFailure, isOnlyRegularFailure, totalFailures } = getCasesParamaters(props);

  if (hasSuccess && !hasFailure) {
    return getSuccessHeading(props);
  }

  if (hasSuccess && hasFailure) {
    return getPartialSuccessHeading(props);
  }

  if (!hasSuccess && hasFailure) {
    return getFailureHeading(props, totalFailures, isOnlyCertFailure, isOnlyRegularFailure);
  }

  return '';
};
